import { useState } from 'react';
import { JoinRequestInfo } from '../services/signalr/types';
import { useRooms } from '../context/RoomContext';
import './JoinRequestModal.css';

interface JoinRequestModalProps {
  requests: JoinRequestInfo[];
  roomId: string;
  onClose: () => void;
}

export function JoinRequestModal({ requests, roomId: _roomId, onClose }: JoinRequestModalProps) {
  const { approveJoinRequest, denyJoinRequest } = useRooms();
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleApprove = async (requestId: string) => {
    setProcessingId(requestId);
    setError(null);
    try {
      await approveJoinRequest(requestId);
    } catch (e) {
      setError('Failed to approve request');
      console.error('Failed to approve:', e);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeny = async (requestId: string) => {
    setProcessingId(requestId);
    setError(null);
    try {
      await denyJoinRequest(requestId);
    } catch (e) {
      setError('Failed to deny request');
      console.error('Failed to deny:', e);
    } finally {
      setProcessingId(null);
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content join-request-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Join Requests</h2>
          <span className="request-count">{requests.length} pending</span>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        {error && (
          <div className="error-message">{error}</div>
        )}

        <div className="requests-list">
          {requests.length === 0 ? (
            <div className="no-requests">
              <p>No pending requests</p>
            </div>
          ) : (
            requests.map((request) => (
              <div key={request.id} className="request-item">
                <div className="requester-info">
                  <div className="requester-avatar">
                    {request.requesterAvatar ? (
                      <img src={request.requesterAvatar} alt={request.requesterDisplayName} />
                    ) : (
                      <span>{request.requesterDisplayName.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div className="requester-details">
                    <span className="requester-name">{request.requesterDisplayName}</span>
                    <span className="request-time">{formatTime(request.requestedAt)}</span>
                  </div>
                </div>

                {request.message && (
                  <p className="request-message">"{request.message}"</p>
                )}

                <div className="request-actions">
                  <button
                    className="btn-deny"
                    onClick={() => handleDeny(request.id)}
                    disabled={processingId === request.id}
                  >
                    Deny
                  </button>
                  <button
                    className="btn-approve"
                    onClick={() => handleApprove(request.id)}
                    disabled={processingId === request.id}
                  >
                    {processingId === request.id ? 'Processing...' : 'Approve'}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
